/**
 * Drill-down binary-tree placement picker — tap one downline member at a time (no depth control).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { binaryTreeAPI } from '../api/binary-tree';
import { THEMED_SURFACE_COLORS as C } from './ui/ThemedSurface';

export type PlacementSelection = {
  userId: number;
  username: string;
  position: 'L' | 'R';
};

export type PlacementStepNode = {
  id: number;
  username: string;
  level_code: string;
  position?: string | null;
  has_left: boolean;
  has_right: boolean;
  left?: { id: number; username: string; level_code: string } | null;
  right?: { id: number; username: string; level_code: string } | null;
};

type Breadcrumb = { id: number; username: string };

function SlotButton({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-lg border px-3 py-2.5 text-xs font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
      style={{
        borderColor: active ? `${C.gold}88` : C.line,
        background: active ? `${C.gold}18` : C.inputBg,
        color: active ? C.gold : C.text,
      }}
    >
      {label}
    </button>
  );
}

export function PlacementTreePicker({
  rootUserId,
  rootUsername,
  selection,
  onSelect,
  initialUserId,
  initialPosition,
}: {
  rootUserId: number;
  rootUsername: string;
  selection: PlacementSelection | null;
  onSelect: (sel: PlacementSelection | null) => void;
  initialUserId?: number;
  initialPosition?: 'L' | 'R';
}) {
  const { t } = useTranslation();
  const [currentUserId, setCurrentUserId] = useState(rootUserId);
  const [breadcrumb, setBreadcrumb] = useState<Breadcrumb[]>([{ id: rootUserId, username: rootUsername }]);
  const [bootstrapped, setBootstrapped] = useState(false);

  const stepQuery = useQuery({
    queryKey: ['binary-tree', 'placement-step', currentUserId],
    queryFn: () => binaryTreeAPI.getPlacementStep({ user_id: currentUserId }) as Promise<PlacementStepNode>,
    staleTime: 15_000,
  });

  const step = stepQuery.data;

  useEffect(() => {
    if (bootstrapped) return;

    if (initialUserId === rootUserId) {
      if (initialPosition) {
        onSelect({ userId: rootUserId, username: rootUsername, position: initialPosition });
      }
      setBootstrapped(true);
      return;
    }

    if (!initialUserId) {
      setBootstrapped(true);
      return;
    }

    void (async () => {
      try {
        const { path } = await binaryTreeAPI.getPlacementPath({ user_id: initialUserId });
        if (path.length > 0) {
          setBreadcrumb(path);
          setCurrentUserId(initialUserId);
        }
        if (initialPosition) {
          const node = path.find((p) => p.id === initialUserId);
          if (node) {
            onSelect({ userId: node.id, username: node.username, position: initialPosition });
          }
        }
      } catch {
        /* stay at root */
      } finally {
        setBootstrapped(true);
      }
    })();
  }, [bootstrapped, initialUserId, initialPosition, rootUserId, rootUsername, onSelect]);

  function navigateTo(userId: number, crumb: Breadcrumb[]) {
    setCurrentUserId(userId);
    setBreadcrumb(crumb);
    onSelect(null);
  }

  function drillTo(child: { id: number; username: string }) {
    navigateTo(child.id, [...breadcrumb, { id: child.id, username: child.username }]);
  }

  function goToCrumb(index: number) {
    const next = breadcrumb.slice(0, index + 1);
    navigateTo(next[next.length - 1]!.id, next);
  }

  const isRoot = currentUserId === rootUserId;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
        {t(
          'registerMember.treeHintDrill',
          '在当前节点点「左区/右区注册」即可。左区始终沿 2.1 滑到最底；右区先 2.2 再沿该线 2.1 滑到最底。2.1 空时必须先落 2.1。',
        )}
      </p>

      {breadcrumb.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {breadcrumb.map((crumb, index) => (
            <span key={crumb.id} className="flex items-center gap-1">
              {index > 0 && <span style={{ color: C.muted }}>›</span>}
              <button
                type="button"
                onClick={() => goToCrumb(index)}
                className="font-semibold transition active:opacity-70"
                style={{ color: index === breadcrumb.length - 1 ? C.gold : C.text }}
              >
                {crumb.username}
              </button>
            </span>
          ))}
        </div>
      )}

      {stepQuery.isError ? (
        <div className="rounded-xl border px-3 py-6 text-center text-xs" style={{ borderColor: C.line, color: '#f87171' }}>
          {t('common.loadFailed', '加载失败，请重试')}
        </div>
      ) : null}

      {stepQuery.isLoading && !step ? (
        <div className="rounded-xl border px-3 py-6 text-center text-xs" style={{ borderColor: C.line, color: C.muted }}>
          {t('common.loading', '加载中…')}
        </div>
      ) : null}

      {step ? (
        <div
          className="rounded-xl border px-3 py-3"
          style={{
            borderColor: isRoot ? `${C.gold}55` : C.line,
            background: isRoot ? `${C.gold}10` : C.inputBg,
          }}
        >
          <div className="mb-3">
            <div className="text-[10px]" style={{ color: C.muted }}>
              {t('registerMember.currentNode', '当前节点')}
            </div>
            <div className="text-sm font-semibold" style={{ color: isRoot ? C.gold : C.text }}>
              {step.username}
            </div>
            <div className="text-[10px]" style={{ color: C.muted }}>
              {step.level_code}
              {step.position ? ` · ${step.position}` : ''}
            </div>
          </div>

          <div className="mb-2 text-[10px] font-semibold" style={{ color: C.muted }}>
            {t('registerMember.registerHere', '在此节点安置新会员')}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SlotButton
              label={
                step.has_left
                  ? t('registerMember.placeLeftSpill', '左区注册（滑落）')
                  : t('registerMember.placeLeft', '+ 左区注册')
              }
              active={selection?.userId === step.id && selection.position === 'L'}
              onClick={() => onSelect({ userId: step.id, username: step.username, position: 'L' })}
            />
            <SlotButton
              label={
                !step.has_left
                  ? t('registerMember.placeRightNeedLeft', '请先落左区 2.1')
                  : step.has_right
                    ? t('registerMember.placeRightSpill', '右区注册（滑落）')
                    : t('registerMember.placeRight', '+ 右区注册')
              }
              active={selection?.userId === step.id && selection.position === 'R'}
              disabled={!step.has_left}
              onClick={() => {
                if (!step.has_left) return;
                onSelect({ userId: step.id, username: step.username, position: 'R' });
              }}
            />
          </div>

          {(step.left || step.right) && (
            <>
              <div className="my-3 h-px" style={{ background: `${C.gold}22` }} />
              <div className="mb-2 text-[10px] font-semibold" style={{ color: C.muted }}>
                {t('registerMember.drillDown', '旗下成员 — 点击进入下一层')}
              </div>
              <div className="flex flex-col gap-2">
                {step.left ? (
                  <button
                    type="button"
                    onClick={() => drillTo(step.left!)}
                    className="flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-xs font-semibold transition active:scale-[0.98]"
                    style={{ borderColor: C.line, background: C.chipBg, color: C.text }}
                  >
                    <span>
                      <span style={{ color: C.gold }}>L</span> · {step.left.username}
                    </span>
                    <span style={{ color: C.muted }}>›</span>
                  </button>
                ) : null}
                {step.right ? (
                  <button
                    type="button"
                    onClick={() => drillTo(step.right!)}
                    className="flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-xs font-semibold transition active:scale-[0.98]"
                    style={{ borderColor: C.line, background: C.chipBg, color: C.text }}
                  >
                    <span>
                      <span style={{ color: C.gold }}>R</span> · {step.right.username}
                    </span>
                    <span style={{ color: C.muted }}>›</span>
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}

      {selection ? (
        <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: `${C.gold}66`, color: C.text }}>
          {t('registerMember.selectedPlacement', {
            user: selection.username,
            zone: selection.position === 'L' ? t('auth.positionLeft', '左区') : t('auth.positionRight', '右区'),
            defaultValue: `已选：在 ${selection.username} 下 ${selection.position === 'L' ? '左区' : '右区'} 注册（含滑落）`,
          })}
        </div>
      ) : (
        <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, color: C.muted }}>
          {t('registerMember.pickPlacement', '请先在网体上选择安置位置')}
        </div>
      )}
    </div>
  );
}
